import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, Shield, BarChart2, Megaphone, X, ChevronDown, ChevronUp, Check, Settings } from 'lucide-react';

const STORAGE_KEY = 'uzmanrapor_cookie_consent';
const FB_PIXEL_ID = '2100332090790836';

/* ─── Consent helpers ────────────────────────────────────────── */
export function getConsent() {
  try { return JSON.parse(localStorage.getItem(STORAGE_KEY)); } catch { return null; }
}

export function hasConsent() {
  return getConsent() !== null;
}

function saveConsent(prefs) {
  const data = { necessary: true, analytics: !!prefs.analytics, marketing: !!prefs.marketing, timestamp: new Date().toISOString() };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
  applyConsent(data);
  return data;
}

export function applyConsent(prefs) {
  if (!prefs) return;

  // Google Consent Mode v2
  if (typeof window.gtag === 'function') {
    window.gtag('consent', 'update', {
      analytics_storage:     prefs.analytics  ? 'granted' : 'denied',
      ad_storage:            prefs.marketing  ? 'granted' : 'denied',
      ad_user_data:          prefs.marketing  ? 'granted' : 'denied',
      ad_personalization:    prefs.marketing  ? 'granted' : 'denied',
    });
  }

  // Facebook Pixel — yalnızca marketing izni varsa yükle
  if (prefs.marketing && !window._fbPixelLoaded) {
    window._fbPixelLoaded = true;
    /* eslint-disable */
    !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */
    window.fbq('init', FB_PIXEL_ID);
    window.fbq('track', 'PageView');
  }
}

/* ─── Toggle switch ──────────────────────────────────────────── */
const Toggle = ({ checked, onChange, disabled }) => (
  <button
    type="button"
    role="switch"
    aria-checked={checked}
    disabled={disabled}
    onClick={() => !disabled && onChange(!checked)}
    className={`relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors duration-200 focus:outline-none ${
      disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
    } ${checked ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`}
  >
    <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform duration-200 mt-0.5 ${checked ? 'translate-x-5' : 'translate-x-0.5'}`} />
  </button>
);

/* ─── Category row ───────────────────────────────────────────── */
const CategoryRow = ({ icon: Icon, color, title, description, checked, onChange, disabled, tag }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-gray-100 dark:border-gray-700 rounded-2xl overflow-hidden">
      <div className="flex items-center gap-3 px-4 py-3.5 bg-white dark:bg-gray-800">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{title}</span>
            {tag && <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400">{tag}</span>}
          </div>
        </div>
        <button type="button" onClick={() => setOpen(o => !o)}
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors p-1">
          {open ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
        <Toggle checked={checked} onChange={onChange} disabled={disabled} />
      </div>
      {open && (
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-700/40 border-t border-gray-100 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{description}</p>
        </div>
      )}
    </div>
  );
};

/* ─── Settings Modal ─────────────────────────────────────────── */
const SettingsModal = ({ onSave, onClose }) => {
  const saved = getConsent();
  const [prefs, setPrefs] = useState({ analytics: saved?.analytics ?? true, marketing: saved?.marketing ?? false });

  const set = (key, val) => setPrefs(p => ({ ...p, [key]: val }));

  return (
    <div className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-white dark:bg-gray-900 rounded-3xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 dark:border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/30 rounded-xl flex items-center justify-center">
              <Settings className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-900 dark:text-white">Çerez Tercihleri</h2>
              <p className="text-xs text-gray-400">Hangi çerezlere izin vereceğinizi seçin</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Categories */}
        <div className="px-6 py-5 space-y-3 max-h-[60vh] overflow-y-auto">
          <CategoryRow
            icon={Shield}
            color="bg-green-50 dark:bg-green-900/30 text-green-600"
            title="Zorunlu Çerezler"
            tag="Her zaman aktif"
            description="Platformun temel işlevlerini yerine getirmesi için zorunludur. Oturum yönetimi, güvenlik ve temel sayfa işlevleri bu çerezlere bağlıdır. Devre dışı bırakılamaz."
            checked={true}
            onChange={() => {}}
            disabled={true}
          />
          <CategoryRow
            icon={BarChart2}
            color="bg-blue-50 dark:bg-blue-900/30 text-blue-600"
            title="Analitik Çerezler"
            description="Google Analytics aracılığıyla sayfa ziyaretlerini, tıklamaları ve kullanım istatistiklerini anonim olarak ölçmemize yardımcı olur. Bu veriler siteyi iyileştirmek için kullanılır; kişisel olarak tanımlanmaz."
            checked={prefs.analytics}
            onChange={v => set('analytics', v)}
            disabled={false}
          />
          <CategoryRow
            icon={Megaphone}
            color="bg-orange-50 dark:bg-orange-900/30 text-orange-500"
            title="Pazarlama Çerezleri"
            description="Facebook Pixel gibi araçlarla reklam performansını ölçmek ve kişiselleştirilmiş reklamlar sunmak için kullanılır. Veriler reklam platformlarıyla paylaşılabilir."
            checked={prefs.marketing}
            onChange={v => set('marketing', v)}
            disabled={false}
          />
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-3 px-6 py-5 border-t border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
          <Link to="/gizlilik-politikasi" onClick={onClose}
            className="text-xs text-blue-600 hover:underline">
            Gizlilik Politikası
          </Link>
          <div className="flex gap-2">
            <button onClick={() => { onSave({ analytics: false, marketing: false }); onClose(); }}
              className="px-4 py-2 text-sm font-medium rounded-xl border border-gray-200 dark:border-gray-700
                text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
              Yalnızca Zorunlu
            </button>
            <button onClick={() => { onSave(prefs); onClose(); }}
              className="px-5 py-2 text-sm font-semibold rounded-xl bg-blue-600 hover:bg-blue-700 text-white transition-colors">
              Tercihleri Kaydet
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─── Main Banner ────────────────────────────────────────────── */
export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [hiding, setHiding] = useState(false);

  useEffect(() => {
    if (!hasConsent()) {
      const t = setTimeout(() => setVisible(true), 800);
      return () => clearTimeout(t);
    } else {
      // Önceki oturumdan kayıtlı tercih varsa uygula
      applyConsent(getConsent());
    }
  }, []);

  const dismiss = useCallback((prefs) => {
    saveConsent(prefs);
    setHiding(true);
    setTimeout(() => { setVisible(false); setHiding(false); }, 350);
  }, []);

  if (!visible) return null;

  return (
    <>
      {/* Banner */}
      <div className={`fixed bottom-0 left-0 right-0 z-[9998] p-3 sm:p-4 transition-transform duration-350 ${hiding ? 'translate-y-full' : 'translate-y-0'}`}
        style={{ animation: !hiding ? 'slideUp .35s ease-out' : undefined }}>
        <style>{`@keyframes slideUp { from { transform: translateY(100%); } to { transform: translateY(0); } }`}</style>

        <div className="max-w-5xl mx-auto bg-white dark:bg-gray-900 rounded-3xl shadow-2xl border border-gray-100 dark:border-gray-800 overflow-hidden">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4 px-5 py-4 sm:px-6">

            {/* Icon + Text */}
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="w-10 h-10 bg-amber-50 dark:bg-amber-900/20 rounded-2xl flex items-center justify-center flex-shrink-0 mt-0.5">
                <Cookie className="h-5 w-5 text-amber-500" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 dark:text-white mb-0.5">
                  Bu site çerez kullanmaktadır
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
                  Deneyiminizi iyileştirmek, analiz yapmak ve pazarlama faaliyetleri için çerezler kullanıyoruz.{' '}
                  <Link to="/gizlilik-politikasi" className="text-blue-600 hover:underline font-medium">
                    Gizlilik Politikası
                  </Link>
                  {' '}ve{' '}
                  <Link to="/kvkk" className="text-blue-600 hover:underline font-medium">
                    KVKK
                  </Link>
                  {' '}belgelerimizi inceleyebilirsiniz.
                </p>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex items-center gap-2 flex-shrink-0 w-full sm:w-auto">
              <button
                onClick={() => setShowModal(true)}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-xl
                  border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400
                  hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex-1 sm:flex-none justify-center">
                <Settings className="h-3.5 w-3.5" /> Ayarlar
              </button>
              <button
                onClick={() => dismiss({ analytics: false, marketing: false })}
                className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-xl
                  border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400
                  hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors flex-1 sm:flex-none justify-center">
                <X className="h-3.5 w-3.5" /> Reddet
              </button>
              <button
                onClick={() => dismiss({ analytics: true, marketing: true })}
                className="flex items-center gap-1.5 px-4 py-2 text-xs font-semibold rounded-xl
                  bg-blue-600 hover:bg-blue-700 text-white transition-colors flex-1 sm:flex-none justify-center shadow-sm">
                <Check className="h-3.5 w-3.5" /> Kabul Et
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Modal */}
      {showModal && (
        <SettingsModal
          onSave={prefs => dismiss(prefs)}
          onClose={() => setShowModal(false)}
        />
      )}
    </>
  );
}
